import { Tile, parseTiles } from '@/engine/tiles';

const t = (s: string): Tile[] => parseTiles(s);

export interface Beat {
  instruct: string; // 튜터 안내(할 일)
  show?: Tile[]; // 상단에 보여줄 손패(조작 불가, 맥락용)
  pool?: Tile[]; // 누를 수 있는 패
  targets?: Tile[]; // 정답 타일값(풀에서 해당 값을 모두 누르면 통과)
  cta?: string; // 버튼으로 통과
  ctaStyle?: 'primary' | 'gold';
  effect?: 'win' | 'riichi'; // 버튼 클릭 시 화면 연출
  note?: string; // 통과 후 칭찬·설명
}

export interface Lesson {
  id: string;
  title: string;
  beats: Beat[];
}

export const LESSONS: Lesson[] = [
  {
    id: 'tiles',
    title: '타일 익히기',
    beats: [
      {
        instruct: '마작 패는 숫자패 3종류와 자패가 있어요. 먼저 **만(萬)** — 숫자 + 萬. 이 줄에서 만패를 모두 누르세요.',
        pool: t('1m5p3s9m1z4p7z'),
        targets: t('1m9m'),
        note: '정답! 아래에 萬이 적힌 게 만패예요.',
      },
      {
        instruct: '**통(筒)** — 동그라미 수패(파란색). 통패를 모두 누르세요.',
        pool: t('2p5s7p1z3m9p'),
        targets: t('2p7p9p'),
        note: '통패는 筒! 1통~9통까지 있어요.',
      },
      {
        instruct: '**삭(索)** — 대나무 수패(초록색). 삭패를 모두 누르세요.',
        pool: t('3s5m1s6z7p9s'),
        targets: t('1s3s9s'),
        note: '삭패는 索! 이제 수패 3종류를 익혔어요.',
      },
      {
        instruct: '**자패**는 바람(東南西北)과 삼원(白發中). 숫자가 없어요. 자패를 모두 누르세요.',
        pool: t('1z5m5z3p2z7z'),
        targets: t('1z5z2z7z'),
        note: '좋아요! 자패는 순서가 없고 같은 것끼리만 모아요.',
      },
    ],
  },
  {
    id: 'meld',
    title: '면자 만들기',
    beats: [
      {
        instruct: '**슌쯔**는 같은 수패의 연속 3장이에요. 2·3·4만으로 슌쯔를 만들어요 — 세 장을 모두 누르세요.',
        pool: t('234m'),
        targets: t('234m'),
        note: '234만 = 슌쯔 완성! 연속이면 돼요(예: 567통).',
      },
      {
        instruct: '**커쯔**는 똑같은 패 3장이에요. 백(白) 3장을 누르세요.',
        pool: t('5z5z5z'),
        targets: t('5z5z5z'),
        note: '白白白 = 커쯔! 같은 패 3장이면 돼요.',
      },
      {
        instruct: '손패는 **면자(슌쯔/커쯔) 4개 + 짝패 1개**로 완성돼요. 바로 다음 레슨에서 직접 완성시켜 볼게요.',
        cta: '알겠어요',
      },
    ],
  },
  {
    id: 'tenpai',
    title: '텐파이',
    beats: [
      {
        instruct:
          '이 손은 한 장만 더 오면 완성! 위 손패를 보세요 — **123만 456만 789만**(면자 3개), **東東**(짝), 그리고 **12통**. 12통이 슌쯔가 되려면 어떤 통패가 와야 할까요? 아래에서 그 패를 누르세요.',
        show: t('123456789m12p1z1z'),
        pool: t('1p2p3p'),
        targets: t('3p'),
        note: '3통! 이렇게 "한 장만 기다리는" 상태가 **텐파이**예요.',
      },
    ],
  },
  {
    id: 'agari',
    title: '화료(완성)',
    beats: [
      {
        instruct:
          '텐파이 상태예요: **123만 456만 789만 123통 + 東**. 東을 한 장 더 가져오면 東東 짝이 완성됩니다. **"패 가져오기"**를 눌러보세요.',
        show: t('123456789m123p1z'),
        cta: '패 가져오기',
      },
      {
        instruct: '東을 가져왔어요! 이제 **면자 4개 + 東東 짝** = 완성형. **쯔모!**를 눌러 화료하세요.',
        show: t('123456789m123p1z1z'),
        cta: '쯔모!',
        ctaStyle: 'primary',
        effect: 'win',
        note: '화료 성공! 스스로 가져와 완성하면 "쯔모", 남이 버린 패로 완성하면 "론"이에요.',
      },
    ],
  },
  {
    id: 'riichi',
    title: '리치',
    beats: [
      {
        instruct:
          '울지 않은(닫힌) 손으로 텐파이! **리치**를 선언하면 1,000점을 걸고 한 방을 노려요(우라도라 보너스 기회도). **리치!**를 눌러보세요.',
        show: t('123456789m12p1z1z'),
        cta: '리치!',
        ctaStyle: 'gold',
        effect: 'riichi',
        note: '리치 선언! 버리는 패가 옆으로 눕고, 이후엔 손을 바꿀 수 없어요.',
      },
      {
        instruct: '리치 뒤엔 3통만 오면 자동으로 화료를 노립니다. 마지막으로 점수가 어떻게 매겨지는지 볼게요.',
        cta: '알겠어요',
      },
    ],
  },
  {
    id: 'score',
    title: '역과 점수',
    beats: [
      {
        instruct:
          '완성된 손엔 **역(役)**이 붙어야 화료할 수 있어요. 이 손은 2~8 숫자패만 있는 **탕야오**(1판)! 위 손패를 보세요.',
        show: t('234567m234p678p55s'),
        cta: '다음',
      },
      {
        instruct:
          '역의 **판수**와 손모양의 **부(符)**로 점수가 정해지고, 테이블 레이트로 가상머니가 돼요. 예: 초급(1점=10원)에서 3,900점 화료 = **39,000원**!',
        cta: '배웠어요',
      },
    ],
  },
];

export function getLesson(id: string): Lesson | undefined {
  return LESSONS.find((l) => l.id === id);
}
